import { FC } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';
import { DATE_TIME_FORMAT } from 'constants/constants';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { EToasterStatus, showToast } from 'utils/showToast';

import Switch from 'components/Switch/Switch';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';

import usePlans from '../../../api/usePlans';
import {
  BillingCyclesUnit,
  CreatePlan,
  UpdatePlan
} from '../../../common/contracts';
import FormHeader from '../../../components/FormHeader/FormHeader';
import Input from '../../../components/Input/Input';
import InputSelectDropdown from '../../../components/InputSelectDropdown/InputSelectDropdown';
import RichText from '../../../components/RichText/RichText';
import {
  BILLING_CYCLES_UNIT_OPTIONS,
  BILLING_PERIOD_UNIT_OPTIONS
} from '../constants';
import {
  CreatePlanFormValues,
  getCreatePlanFromInitialValues,
  getPlanSchema} from '../utils';

type SubscriptionsFormProps = {
  edit?: boolean;
};

export const CreatePlanForm: FC<SubscriptionsFormProps> = ({
  edit = false
}) => {
  const navigate = useNavigate();

  const { planId } = useParams();
  const { createPlan, getPlan, updatePlan } = usePlans(undefined, planId);

  const {
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    submitForm,
    isValid,
    dirty,
    setFieldValue
  } = useFormik({
    validateOnMount: true,
    validationSchema: getPlanSchema(),
    enableReinitialize: true,
    initialValues: getCreatePlanFromInitialValues(getPlan.data),
    onSubmit: async (values) => {
      if (edit) {
        updateExistingPlan(values);
      } else {
        createNewPlan(values);
      }
    }
  });

  const createNewPlan = async (values: CreatePlanFormValues) => {
    const newPlan: CreatePlan = {
      name: values.name,
      displayName: values.displayName,
      description: values.description,
      billingPeriodUnit: values.billingPeriodUnit,
      billingCycles:
        values.billingCyclesUnit === BillingCyclesUnit.FIXED_NUMBER_OF_PAYMENTS
          ? values.billingCycles
          : undefined,
      basePriceInUsd: values.price * 100,
      billingCyclesUnit: values.billingCyclesUnit,
      firstPaymentDiscount:
        (values.isFirstPaymentDiscountEnabled && values.firstPaymentDiscount) ||
        undefined
    };

    await createPlan.mutateAsync(newPlan, {
      onSuccess: () => {
        showToast({
          message: 'Plan created successfully',
          status: EToasterStatus.SUCCESS
        });
        navigate('../');
      },
      onError: () => {
        showToast({
          message: 'Error creating plan',
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  const updateExistingPlan = async (values: CreatePlanFormValues) => {
    const updatedPlan: UpdatePlan = {
      name: values.name,
      displayName: values.displayName,
      billingPeriodUnit: values.billingPeriodUnit,
      billingCycles:
        values.billingCyclesUnit === BillingCyclesUnit.FIXED_NUMBER_OF_PAYMENTS
          ? values.billingCycles
          : undefined,
      billingCyclesUnit: values.billingCyclesUnit,
      basePriceInUsd: values.price * 100,
      firstPaymentDiscount:
        (values.isFirstPaymentDiscountEnabled && values.firstPaymentDiscount) ||
        undefined
    };

    updatePlan.mutateAsync(
      { planId: planId!, planData: updatedPlan },
      {
        onSuccess: () => {
          navigate('../');
          showToast({
            message: 'Plan updated successfully',
            status: EToasterStatus.SUCCESS
          });
        },
        onError: () => {
          showToast({
            message: 'Error updating plan',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const isLoadingEdit = edit && getPlan.isLoading;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        onCloseForm={() => navigate('../')}
        closeFormOnSubmit={false}
        onMainButtonClick={() => submitForm()}
        title={`${edit ? 'Edit plan' : 'Create new plan'} `}
        isFormValid={
          isValid && dirty && !updatePlan.isLoading && !createPlan.isLoading
        }
        mainButtonText="Save"
      />

      <div className="mt-7 w-full mx-auto overflow-y-auto h-[calc(100vh-160px)]">
        <div className="w-[456px] mx-auto">
          {isLoadingEdit && (
            <div className="flex justify-center items-center w-full h-[200px]">
              <CircularProgress className="h-12" />
            </div>
          )}
          {!isLoadingEdit && (
            <>
              <RichText
                title={edit ? 'Edit plan' : 'Create new plan'}
                description={
                  edit ? (
                    <>
                      <div>
                        Created at:{' '}
                        {dayjs(getPlan.data?.createdAt).format(
                          DATE_TIME_FORMAT
                        )}
                      </div>
                      <div>Plan ID Number: {planId}</div>
                    </>
                  ) : (
                    'Create a plan to offer recurring payments to your users.'
                  )
                }
                className="mb-9"
              />
              <div className="flex flex-col gap-5 mb-4">
                <div className="grid grid-cols-1 gap-5">
                  <Input
                    disabled={edit}
                    label="Plan Name"
                    placeholder="Plan name"
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isRequired
                    infoTooltipContent="An internal name for your subscription plan. Not visible to users at checkout."
                    state={touched.name && errors.name ? 'error' : 'default'}
                    description={touched.name && errors.name ? errors.name : ''}
                  />

                  <Input
                    label="Plan Display Name"
                    placeholder="Display name"
                    type="text"
                    name="displayName"
                    value={values.displayName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isRequired
                    infoTooltipContent="The name of your subscription plan shown to users at checkout."
                    state={
                      touched.displayName && errors.displayName
                        ? 'error'
                        : 'default'
                    }
                    description={
                      touched.displayName && errors.displayName
                        ? errors.displayName
                        : ''
                    }
                  />

                  <Input
                    label="Description"
                    placeholder="Description"
                    type="text"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    state={
                      touched.description && errors.description
                        ? 'error'
                        : 'default'
                    }
                    description={
                      touched.description && errors.description
                        ? errors.description
                        : ''
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <InputSelectDropdown
                    isRequired
                    label="Billing Frequency"
                    options={BILLING_PERIOD_UNIT_OPTIONS}
                    buttonDataTestId="dashPlansBillingFrequency"
                    defaultSelected={BILLING_PERIOD_UNIT_OPTIONS.find(
                      (option) => option.value === values.billingPeriodUnit
                    )}
                    onOptionChange={(newValue: any) => {
                      setFieldValue('billingPeriodUnit', newValue);
                    }}
                  />

                  <Input
                    label="Price"
                    placeholder="29.99"
                    type="number"
                    name="price"
                    value={values.price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isRequired
                    state={touched.price && errors.price ? 'error' : 'default'}
                    description={
                      touched.price && errors.price ? errors.price : ''
                    }
                    leftIcon={<span>$</span>}
                    leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
                    min={0.8}
                    step={0.1}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-5 mb-9 w-full">
                <div className="grid grid-cols-2 gap-5">
                  <Tooltip {...(!edit ? { open: false } : {})}>
                    <TooltipTrigger asChild>
                      <div className="inline-block">
                        <InputSelectDropdown
                          disabled={edit}
                          label="Billing Cycles"
                          buttonDataTestId="dashPlansBillingCycles"
                          isRequired
                          defaultSelected={BILLING_CYCLES_UNIT_OPTIONS.find(
                            (option) =>
                              option.value === values.billingCyclesUnit
                          )}
                          options={BILLING_CYCLES_UNIT_OPTIONS}
                          placeholder={'Fixed number of payments'}
                          onOptionChange={(newValue: any) =>
                            setFieldValue('billingCyclesUnit', newValue)
                          }
                        />
                      </div>
                    </TooltipTrigger>
                    <BillingCyclesTooltipContent />
                  </Tooltip>

                  <Tooltip
                    {...(!(
                      edit &&
                      values.billingCyclesUnit === BillingCyclesUnit.ONGOING
                    )
                      ? { open: false }
                      : {})}
                  >
                    <TooltipTrigger asChild>
                      <div className="inline-block">
                        <Input
                          label="&nbsp;"
                          type="number"
                          name="billingCycles"
                          disabled={
                            values.billingCyclesUnit ===
                            BillingCyclesUnit.ONGOING
                          }
                          value={values.billingCycles}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          state={
                            touched.billingCycles && errors.billingCycles
                              ? 'error'
                              : 'default'
                          }
                          description={
                            touched.billingCycles && errors.billingCycles
                              ? errors.billingCycles
                              : ''
                          }
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'Minus') {
                              e.preventDefault();
                            }
                          }}
                          min={1}
                        />
                      </div>
                    </TooltipTrigger>
                    <BillingCyclesTooltipContent />
                  </Tooltip>
                </div>
              </div>
              <Switch
                data-testid="dashPlansFirstPaymentDiscountSwitch"
                label="First Payment Discount"
                description="Apply a discount to the user’s first payment for this plan"
                defaultChecked={values.isFirstPaymentDiscountEnabled}
                textAlignment="right"
                size="lg"
                onCheckedChange={() => {
                  setFieldValue(
                    'isFirstPaymentDiscountEnabled',
                    !values.isFirstPaymentDiscountEnabled
                  );
                }}
              />
              <Input
                label=""
                placeholder="29.99"
                type="number"
                name="firstPaymentDiscount"
                value={values.firstPaymentDiscount}
                disabled={!values.isFirstPaymentDiscountEnabled}
                onChange={handleChange}
                isRequired
                state={
                  touched.firstPaymentDiscount && errors.firstPaymentDiscount
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.firstPaymentDiscount && errors.firstPaymentDiscount
                    ? errors.firstPaymentDiscount
                    : ''
                }
                leftIcon={<span>%</span>}
                leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
                min={1}
                max={90}
                step={1}
              />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

function BillingCyclesTooltipContent() {
  return (
    <TooltipContent
      side="top"
      align="center"
      className="rounded-md border border-base-popover bg-base-popover shadow-md py-[6px] px-[12px]"
    >
      <span className="text-p-s text-base-mutedForeground">
        To change between a fixed number of payments and an ongoing
        subscription, please create a new plan.
      </span>
    </TooltipContent>
  );
}
